import { useCallback } from "react";

import { useObjectMemo } from "../../../hooks";
import {
  useRequestWithFeedback,
  parseErrorResponse,
} from "../../../composites";
import { useLibrary } from "../../../providers";

import { API } from "./api";

export const useFetchMappings = () => {
  const { send, loading, status, data } = useRequestWithFeedback<{
    results: API.ACHImport.CSVFieldMapping[];
  }>();

  const fetchMappings = useCallback(() => {
    return send({
      action: API.fetchMappings(),
    });
  }, [send]);

  return useObjectMemo({
    error: status === "error",
    send: fetchMappings,
    loading,
    data,
  });
};

export const useCreateMapping = () => {
  const { send, loading } =
    useRequestWithFeedback<API.ACHImport.CSVFieldMapping>();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");

  const createMapping = useCallback(
    (payload: API.ACHImport.CSVFieldMappingRequest) => {
      return send({
        action: API.createMapping(payload),
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          throwToast({
            kind: "error",
            message: errors.length
              ? `${errors[0].description}`
              : l10n.getString(
                  "column-mapping-generic-error",
                  null,
                  "We're having trouble completing your request. Please try again later.",
                ),
          });
        },
      });
    },
    [send, throwToast, l10n],
  );

  return useObjectMemo({
    createMapping,
    loading,
  });
};

export const useUpdateMapping = () => {
  const { send, loading } =
    useRequestWithFeedback<API.ACHImport.CSVFieldMapping>();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");

  const updateMapping = useCallback(
    (uuid: string, payload: API.ACHImport.CSVFieldMappingRequest) => {
      return send({
        action: API.updateMapping(uuid, payload),
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          throwToast({
            kind: "error",
            message: errors.length
              ? `${errors[0].description}`
              : l10n.getString(
                  "column-mapping-generic-error",
                  null,
                  "We're having trouble completing your request. Please try again later.",
                ),
          });
        },
      });
    },
    [send, throwToast, l10n],
  );

  return useObjectMemo({
    updateMapping,
    loading,
  });
};

export const useDeleteMapping = () => {
  const { send, loading } = useRequestWithFeedback();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");

  const deleteMapping = useCallback(
    (uuid: string) => {
      return send({
        action: API.deleteMapping(uuid),
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          throwToast({
            kind: "error",
            message: errors.length
              ? `${errors[0].description}`
              : l10n.getString(
                  "column-mapping-generic-error",
                  null,
                  "We're having trouble completing your request. Please try again later.",
                ),
          });
        },
      });
    },
    [send, throwToast, l10n],
  );

  return useObjectMemo({
    deleteMapping,
    loading,
  });
};

export const useParseCSV = () => {
  const { send, loading, status } =
    useRequestWithFeedback<API.ACHImport.CSVParseResponse>();

  const parseCSV = useCallback(
    (payload: API.ACHImport.CSVParseRequest) => {
      return send({
        action: API.parseCSV(payload),
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    parseCSV,
    loading,
  });
};
